import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { createMission, updateMission, getMission, getAvailableChauffeurs, analyzeMission } from '../../services/missionService';
import clientService from '../../services/clientService';
import api from '../../services/api';
import GoogleAutocomplete from '../../components/map/GoogleAutocomplete';
import { useUser } from '../../context/UserContext';
import debounce from 'lodash.debounce';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FaBrain } from 'react-icons/fa';
import './MissionForm.css';
import { useI18n } from '../../i18n/I18nContext';
import '../agent/AgentControlCenter.css'; // Premium styles

const MissionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userType, userId } = useUser();
  const { t } = useI18n(); // Get the translation function
  const isEditMode = Boolean(id);

  // State for location place objects
  const [departurePlace, setDeparturePlace] = useState(null);
  const [arrivalPlace, setArrivalPlace] = useState(null);

  const [formData, setFormData] = useState({
    departureLocation: '',
    arrivalLocation: '',
    departureDate: '',
    departureTime: '',
    arrivalDate: '',
    arrivalTime: '',
    plannedStartDate: '',
    plannedStartTime: '',
    plannedEndDate: '',
    plannedEndTime: '',
    cargoType: '',
    vehicleType: '',
    weight: '',
    length: '',
    volume: '',
    price: '',
    currency: 'EUR',
    referenceNumber: '',
    description: '',
    expediteurNom: '',
    expediteurContact: '',
    expediteurTelephone: '',
    destinataireNom: '',
    destinataireContact: '',
    destinataireTelephone: '',
    isDangerousMaterial: false,
    isInternational: false,
    requiredEquipment: [],
    clientId: ''
  });

  // Mapping cargo type -> predefined vehicle type options
  const vehicleOptionsByCargo = {
    BENNES: ['cerealiere', 'renforcee', 'fondMouvant', 'autre', 'tout'],
    CITERNES: ['pulveeLiquide', 'autre', 'tout'],
    CONTAINERS: ['20', '40', 'tout'],
    DEMENAGEMENTS: ['cat1', 'cat2', 'cat3', 'tout'],
    MARCHANDISES_GENERALES: ['taut', 'fourgon', 'plateau', 'autre', 'tout'],
    TEMPERATURE_DIRIGEE: ['frais', 'surgele', 'nonRefrigere', 'multiTemp', 'autre', 'tout'],
    EXPRESSE_ENVELOPPE: ['vehiculeLeger', 'moto', 'tout'],
    EXPRESSE_COLIS: ['vehiculeLeger', 'moto', 'tout'],
    MESSAGERIE_COURSES: ['neant'],
    TRANSPORTS_EXCEPTIONNEL: ['neant'],
    TRANSPORTS_VOLUMINEUX: ['neant'],
  };

  const currentVehicleOptions = vehicleOptionsByCargo[formData.cargoType] || [];

  const generateReferenceNumber = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 9; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const [availableChauffeurs, setAvailableChauffeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [initLoading, setInitLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const [showOcrModal, setShowOcrModal] = useState(false);
  const [ocrExtractedFields, setOcrExtractedFields] = useState(null);

  // Handle location selection with debounce
  const handleDepartureSelect = useCallback((place) => {
    setDeparturePlace(place);
    if (place) {
      // Mapbox returns coordinates as [lng, lat] in place.center or place.coordinates
      const coords = place.coordinates || place.center || [null, null];
      setFormData(prev => ({
        ...prev,
        departureLocation: place.name || place.place_name,
        departureLat: coords[1],
        departureLng: coords[0]
      }));
    }
  }, []);

  const handleArrivalSelect = useCallback((place) => {
    setArrivalPlace(place);
    if (place) {
      const coords = place.coordinates || place.center || [null, null];
      setFormData(prev => ({
        ...prev,
        arrivalLocation: place.name || place.place_name,
        arrivalLat: coords[1],
        arrivalLng: coords[0]
      }));
    }
  }, []);

  const parseDateTime = (value) => {
    if (!value || typeof value !== 'string') {
      return { date: '', time: '' };
    }
    const [date, time] = value.split('T');
    return {
      date: date || '',
      time: time ? time.substring(0, 5) : ''
    };
  };

  const onDepartureInputChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      departureLocation: value,
      departureLat: undefined,
      departureLng: undefined
    }));
  }, []);

  const onArrivalInputChange = useCallback((e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      arrivalLocation: value,
      arrivalLat: undefined,
      arrivalLng: undefined
    }));
  }, []);

  useEffect(() => {
    // Check if user has permission to create/edit missions
    if (!['expediteur', 'transporteur', 'responsable_transporteur'].includes(userType)) {
      setError(t('missionForm.errors.noPermission'));
      setInitLoading(false);
      return;
    }

    const initializeData = async () => {
      try {
        setInitLoading(true);
        // Only fetch chauffeurs and clients for transporters (and managers)
        if (userType === 'transporteur' || userType === 'responsable_transporteur') {
          await fetchAvailableChauffeurs();
          await fetchClients();
        }
        if (isEditMode) {
          await fetchMissionData();
        } else {
          setCanEdit(true);
          // Generate reference number for new missions
          setFormData(prev => ({ ...prev, referenceNumber: generateReferenceNumber() }));
        }
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message || t('missionForm.errors.initFailed'));
      } finally {
        setInitLoading(false);
      }
    };

    initializeData();
  }, [id, userType, userId, t]);

  const handleRunAnalysis = async () => {
    if (!id) return;
    setAnalysisLoading(true);
    setAnalysisResult('');
    setShowAnalysisModal(true);
    try {
      const data = await analyzeMission(id);
      setAnalysisResult(data?.analysis || data?.text || 'Aucune analyse disponible.');
    } catch (err) {
      setAnalysisResult(`Erreur : ${err.message}`);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const [ocrFileName, setOcrFileName] = useState(null);

  const handleOcrExtraction = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    setOcrError(null);
    setOcrFileName(file.name);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/agent/mission-ocr/stage', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};
      const fields = data.missionFields || {};
      // Store extracted fields and show preview modal
      setOcrExtractedFields(fields);
      setShowOcrModal(true);
    } catch (err) {
      console.error('AI OCR error:', err);
      setOcrError(t('missionForm.errors.ocrFailed') || "Erreur lors de l'extraction OCR.");
      setOcrFileName(null);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleConfirmOcr = () => {
    const fields = ocrExtractedFields || {};

    // Helper to format any date string into YYYY-MM-DD for the <input type="date">
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      try {
        // If it's already YYYY-MM-DD, just return it
        const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (ymdMatch) return ymdMatch[0];
        
        // If it's DD/MM/YYYY
        const dmyMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;

        // Fallback to JS Date parser (works for ISO strings like 2026-06-19T...)
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          return d.toISOString().split('T')[0];
        }
      } catch (e) {
        console.warn("Could not parse OCR date:", dateStr);
      }
      return dateStr; // Fallback to raw string if parsing fails
    };

    setFormData(prev => ({
      ...prev,
      departureLocation:   fields.departureLocation   || prev.departureLocation,
      arrivalLocation:     fields.arrivalLocation     || prev.arrivalLocation,
      departureDate:       fields.departureDate ? formatDateForInput(fields.departureDate) : prev.departureDate,
      arrivalDate:         fields.arrivalDate   ? formatDateForInput(fields.arrivalDate)   : prev.arrivalDate,
      weight:              fields.weight               || prev.weight,
      length:              fields.length               || prev.length,
      volume:              fields.volume               || prev.volume,
      price:               fields.price                || prev.price,
      description:         fields.description          || prev.description,
      referenceNumber:     fields.referenceNumber      || prev.referenceNumber,
      currency:            fields.currency             || prev.currency,
      cargoType:           fields.cargoType            || prev.cargoType,
      vehicleType:         fields.vehicleType          || prev.vehicleType,
      isDangerousMaterial: fields.dangerousMaterial != null ? fields.dangerousMaterial : prev.isDangerousMaterial,
    }));
    setShowOcrModal(false);
    setSuccess('Données extraites et appliquées avec succès.');
  };

  const handleOcrFileInputChange = (e) => {
    if (e.target.files && e.target.files.length) {
      handleOcrExtraction(e.target.files[0]);
      e.target.value = '';
    }
  };

  const handleOcrDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      handleOcrExtraction(e.dataTransfer.files[0]);
    }
  };

  const handleOcrDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const fetchMissionData = async () => {
    try {
      const mission = await getMission(id);

      // Defensive: handle both date and time fields
      let departureDate = '';
      let departureTime = '';
      if (mission.departureDate && typeof mission.departureDate === 'string') {
        const [date, time] = mission.departureDate.split('T');
        departureDate = date;
        departureTime = time ? time.substring(0, 5) : '';
      }

      let arrivalDate = '';
      let arrivalTime = '';
      if (mission.arrivalDate && typeof mission.arrivalDate === 'string') {
        const [date, time] = mission.arrivalDate.split('T');
        arrivalDate = date;
        arrivalTime = time ? time.substring(0, 5) : '';
      }

      const plannedStart = parseDateTime(mission.plannedStartAt);
      const plannedEnd = parseDateTime(mission.plannedEndAt);

      setFormData({
        ...formData, // keep defaults for missing fields
        ...mission,
        departureDate,
        departureTime,
        arrivalDate,
        arrivalTime,
        plannedStartDate: plannedStart.date,
        plannedStartTime: plannedStart.time,
        plannedEndDate: plannedEnd.date,
        plannedEndTime: plannedEnd.time,
        requiredEquipment: Array.isArray(mission.requiredEquipment)
          ? mission.requiredEquipment
          : (mission.requiredEquipment ? mission.requiredEquipment.split(',').map(item => item.trim()) : []),
        currency: mission.currency || 'EUR',
        expediteurNom: mission.expediteurNom || '',
        expediteurContact: mission.expediteurContact || '',
        expediteurTelephone: mission.expediteurTelephone || '',
        clientId: mission.clientId ? mission.clientId.toString() : ''
      });

      // Determine edit permissions: only creator can edit
      try {
        const missionCreatorType = (mission.creatorType || '').toString().toUpperCase();
        const currentUserType = (userType || '').toString().toUpperCase();
        const isTransporteurType = (type) => type === 'TRANSPORTEUR' || type === 'RESPONSABLE_TRANSPORTEUR';
        
        if (isTransporteurType(missionCreatorType) && isTransporteurType(currentUserType)) {
            // The mission was created by a transporteur and the current user is a transporteur.
            // Since the backend already restricts getMission() to the user's own enterprise group,
            // we can safely allow editing for any internal group mission they can fetch.
            setCanEdit(true);
        } else {
            const isTypeMatch = missionCreatorType === currentUserType;
            const isIdMatch = mission.creatorId != null && String(mission.creatorId) === String(userId);
            setCanEdit(isTypeMatch && isIdMatch);
        }
      } catch (_) {
        setCanEdit(false);
      }
    } catch (err) {
      throw new Error(err.message || t('missionForm.errors.fetchFailed'));
    }
  };

  const fetchAvailableChauffeurs = async () => {
    try {
      const chauffeurs = await getAvailableChauffeurs();
      setAvailableChauffeurs(chauffeurs);
    } catch (err) {
      throw new Error(err.message || t('missionForm.errors.fetchChauffeursFailed'));
    }
  };

  const fetchClients = async () => {
    try {
      const data = await clientService.getAllClients();
      setClients(data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === "isDangerousMaterial" || name === "isInternational") ? value === "true" : value
    }));
  };

  const validateForm = () => {
    const requiredFields = {
      departureLocation: t('missionForm.fields.departureLocation'),
      arrivalLocation: t('missionForm.fields.arrivalLocation'),
      departureDate: t('missionForm.fields.departureDate'),
      departureTime: t('missionForm.fields.departureTime'),
      arrivalDate: t('missionForm.fields.arrivalDate'),
      arrivalTime: t('missionForm.fields.arrivalTime'),
      cargoType: t('missionForm.fields.cargoType'),
      vehicleType: t('missionForm.fields.vehicleType'),
      weight: t('missionForm.fields.weight'),
      length: t('missionForm.fields.length')
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        setError(t('missionForm.errors.fieldRequired', { field: label }));
        return false;
      }
    }

    // Validate dates
    const departureDateTime = new Date(`${formData.departureDate}T${formData.departureTime}`);
    const arrivalDateTime = new Date(`${formData.arrivalDate}T${formData.arrivalTime}`);

    if (departureDateTime >= arrivalDateTime) {
      setError(t('missionForm.errors.invalidDates'));
      return false;
    }

    const hasPlannedStartInput = formData.plannedStartDate || formData.plannedStartTime;
    const hasPlannedEndInput = formData.plannedEndDate || formData.plannedEndTime;
    if (hasPlannedStartInput || hasPlannedEndInput) {
      const plannedFieldsComplete = formData.plannedStartDate && formData.plannedStartTime &&
        formData.plannedEndDate && formData.plannedEndTime;
      if (!plannedFieldsComplete) {
        setError(t('missionForm.errors.plannedRangeIncomplete'));
        return false;
      }
      const plannedStartDateTime = new Date(`${formData.plannedStartDate}T${formData.plannedStartTime}`);
      const plannedEndDateTime = new Date(`${formData.plannedEndDate}T${formData.plannedEndTime}`);
      if (plannedStartDateTime >= plannedEndDateTime) {
        setError(t('missionForm.errors.plannedStartAfterEnd'));
        return false;
      }
    }

    // Validate numeric fields
    if (formData.weight <= 0) {
      setError(t('missionForm.errors.invalidWeight'));
      return false;
    }
    if (formData.length <= 0) {
      setError(t('missionForm.errors.invalidLength'));
      return false;
    }
    if (formData.volume && formData.volume <= 0) {
      setError(t('missionForm.errors.invalidVolume'));
      return false;
    }
    if (formData.price && formData.price < 0) {
      setError(t('missionForm.errors.invalidPrice'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!canEdit) {
      setError(t('missionForm.errors.noEditPermission'));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const hasPlannedWindow = formData.plannedStartDate && formData.plannedStartTime &&
        formData.plannedEndDate && formData.plannedEndTime;

      const missionData = {
        departureLocation: formData.departureLocation,
        arrivalLocation: formData.arrivalLocation,
        departureDate: `${formData.departureDate}T${formData.departureTime}:00.000Z`,
        arrivalDate: `${formData.arrivalDate}T${formData.arrivalTime}:00.000Z`,
        cargoType: formData.cargoType,
        vehicleType: formData.vehicleType,
        weight: parseFloat(formData.weight),
        length: parseFloat(formData.length),
        volume: formData.volume ? parseFloat(formData.volume) : null,
        price: formData.price ? parseFloat(formData.price) : null,
        currency: formData.currency,
        referenceNumber: formData.referenceNumber,
        description: formData.description,
        expediteurNom: formData.expediteurNom,
        expediteurContact: formData.expediteurContact,
        expediteurTelephone: formData.expediteurTelephone,
        destinataireNom: formData.destinataireNom,
        destinataireContact: formData.destinataireContact,
        destinataireTelephone: formData.destinataireTelephone,
        isDangerousMaterial: formData.isDangerousMaterial,
        isInternational: formData.isInternational,
        requiredEquipment: formData.requiredEquipment,
        clientId: formData.clientId ? parseInt(formData.clientId) : null,
        departureLat: formData.departureLat ? parseFloat(formData.departureLat) : null,
        departureLng: formData.departureLng ? parseFloat(formData.departureLng) : null,
        arrivalLat: formData.arrivalLat ? parseFloat(formData.arrivalLat) : null,
        arrivalLng: formData.arrivalLng ? parseFloat(formData.arrivalLng) : null
      };

      if (hasPlannedWindow) {
        missionData.plannedStartAt = `${formData.plannedStartDate}T${formData.plannedStartTime}:00.000Z`;
        missionData.plannedEndAt = `${formData.plannedEndDate}T${formData.plannedEndTime}:00.000Z`;
      } else {
        missionData.plannedStartAt = null;
        missionData.plannedEndAt = null;
      }

      if (isEditMode) {
        await updateMission(id, missionData);
        setSuccess(t('missionForm.success.updated'));
      } else {
        await createMission(missionData);
        setSuccess(t('missionForm.success.created'));
      }

      // Redirect after a short delay to show success message
      setTimeout(() => {
        if (isEditMode && userType !== 'expediteur') {
          window.location.href = `/missions/assign?missionId=${id}`;
        } else {
          const dest = userType === 'expediteur' ? '/expediteur/missions' : '/missions';
          window.location.href = dest;
        }
      }, 1500);
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err.message || t('missionForm.errors.submissionFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Script loading is no longer needed with Mapbox
  const handleScriptLoad = useCallback(() => {
    // No-op since we're not using Google Maps script loading anymore
  }, []);

  if (initLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mission-form-container">
        <div className="mission-form-card">
          <div className="mission-form-header">
            <h2 className="mission-form-title">
              {isEditMode ?
                (canEdit ? t('missionForm.title.edit') : t('missionForm.title.view')) :
                t('missionForm.title.create')
              }
            </h2>
            {isEditMode && (
              <Button
                variant="outline-primary"
                className="rounded-pill px-3 shadow-sm d-flex align-items-center gap-2"
                onClick={handleRunAnalysis}
                disabled={analysisLoading}
              >
                <FaBrain /> {t('common.aiAnalyse') || 'Analyse IA'}
              </Button>
            )}

          </div>

          {error && (
            <div className="alert alert-danger alert-custom" role="alert">
              {error}
            </div>
          )}



          {success && (
            <div className="alert alert-success alert-custom" role="alert">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mission-form-grid">
            {/* Left Column: Location & Schedule */}
            <div className="form-column">
              <div className="form-section">
                <h3 className="section-title mb-2">
                  <i className="bi bi-geo-alt"></i> {t('missionForm.sections.location')}
                </h3>

                {/* Type de transport (National / International) */}
                <div className="btn-group w-100 mb-3 shadow-sm" role="group" aria-label="Type de transport">
                  <input
                    type="radio"
                    className="btn-check"
                    name="isInternational"
                    id="typeNational"
                    value="false"
                    checked={formData.isInternational === false}
                    onChange={handleChange}
                    disabled={!canEdit}
                  />
                  <label className="btn btn-outline-primary py-2 fw-semibold" htmlFor="typeNational">
                    <i className="bi bi-house-door me-2"></i>National
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="isInternational"
                    id="typeInternational"
                    value="true"
                    checked={formData.isInternational === true}
                    onChange={handleChange}
                    disabled={!canEdit}
                  />
                  <label className="btn btn-outline-primary py-2 fw-semibold" htmlFor="typeInternational">
                    <i className="bi bi-globe me-2"></i>International
                  </label>
                </div>

                {/* Bloc Chargement (Départ) */}
                <div className="border rounded p-3 mb-3 mt-0">
                  <h5 className="mb-3 fw-bold">
                    {t('missionForm.sections.departure', 'Chargement (Départ)')}
                  </h5>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="expediteurNom"
                          name="expediteurNom"
                          value={formData.expediteurNom}
                          onChange={handleChange}
                          placeholder={t('missionForm.fields.expediteurNom', 'Entreprise de chargement')}
                          disabled={!canEdit}
                        />
                        <label htmlFor="expediteurNom">{t('missionForm.fields.expediteurNom', 'Entreprise de chargement')}</label>
                      </div>
                    </div>
                    
                    <div className="col-md-12">
                      <div className="location-input-container rounded">
                        <GoogleAutocomplete
                          value={formData.departureLocation}
                          onChange={onDepartureInputChange}
                          onSelect={handleDepartureSelect}
                          placeholder={`${t('missionForm.placeholders.departureLocation')} *`}
                          disabled={!canEdit}
                          required
                          inputClassName="form-control py-3"
                          dropdownClassName="border rounded shadow-sm"
                          instanceId="departure"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="expediteurContact"
                          name="expediteurContact"
                          value={formData.expediteurContact}
                          onChange={handleChange}
                          placeholder={t('missionForm.fields.expediteurContact', 'Contact sur place')}
                          disabled={!canEdit}
                        />
                        <label htmlFor="expediteurContact">{t('missionForm.fields.expediteurContact', 'Contact sur place')}</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="tel"
                          className="form-control"
                          id="expediteurTelephone"
                          name="expediteurTelephone"
                          value={formData.expediteurTelephone}
                          onChange={handleChange}
                          placeholder={t('missionForm.fields.expediteurTelephone', 'Téléphone')}
                          disabled={!canEdit}
                        />
                        <label htmlFor="expediteurTelephone">{t('missionForm.fields.expediteurTelephone', 'Téléphone')}</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bloc Livraison (Arrivée) */}
                <div className="border rounded p-3 mb-2">
                  <h5 className="mb-3 fw-bold">
                    {t('missionForm.sections.arrival', 'Livraison (Arrivée)')}
                  </h5>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="destinataireNom"
                          name="destinataireNom"
                          value={formData.destinataireNom}
                          onChange={handleChange}
                          placeholder={t('missionForm.fields.destinataireNom', 'Entreprise de livraison')}
                          disabled={!canEdit}
                        />
                        <label htmlFor="destinataireNom">{t('missionForm.fields.destinataireNom', 'Entreprise de livraison')}</label>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="location-input-container rounded">
                        <GoogleAutocomplete
                          value={formData.arrivalLocation}
                          onChange={onArrivalInputChange}
                          onSelect={handleArrivalSelect}
                          placeholder={`${t('missionForm.placeholders.arrivalLocation')} *`}
                          disabled={!canEdit}
                          required
                          inputClassName="form-control py-3"
                          dropdownClassName="border rounded shadow-sm"
                          instanceId="arrival"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="destinataireContact"
                          name="destinataireContact"
                          value={formData.destinataireContact}
                          onChange={handleChange}
                          placeholder={t('missionForm.fields.destinataireContact', 'Contact sur place')}
                          disabled={!canEdit}
                        />
                        <label htmlFor="destinataireContact">{t('missionForm.fields.destinataireContact', 'Contact sur place')}</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="tel"
                          className="form-control"
                          id="destinataireTelephone"
                          name="destinataireTelephone"
                          value={formData.destinataireTelephone}
                          onChange={handleChange}
                          placeholder={t('missionForm.fields.destinataireTelephone', 'Téléphone')}
                          disabled={!canEdit}
                        />
                        <label htmlFor="destinataireTelephone">{t('missionForm.fields.destinataireTelephone', 'Téléphone')}</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">
                  <i className="bi bi-calendar-event"></i> {t('missionForm.sections.schedule')}
                </h3>
                <div className="date-time-group">
                  <div className="form-floating">
                    <input
                      type="date"
                      className="form-control"
                      id="departureDate"
                      name="departureDate"
                      value={formData.departureDate}
                      onChange={handleChange}
                      required
                      disabled={!canEdit}
                    />
                    <label htmlFor="departureDate">{t('missionForm.fields.departureDate')} <span className="required-field">*</span></label>
                  </div>
                  <div className="form-floating">
                    <input
                      type="time"
                      className="form-control"
                      id="departureTime"
                      name="departureTime"
                      value={formData.departureTime}
                      onChange={handleChange}
                      required
                      disabled={!canEdit}
                    />
                    <label htmlFor="departureTime">{t('missionForm.fields.departureTime')} <span className="required-field">*</span></label>
                  </div>
                </div>

                <div className="date-time-group mb-0">
                  <div className="form-floating">
                    <input
                      type="date"
                      className="form-control"
                      id="arrivalDate"
                      name="arrivalDate"
                      value={formData.arrivalDate}
                      onChange={handleChange}
                      required
                      disabled={!canEdit}
                    />
                    <label htmlFor="arrivalDate">{t('missionForm.fields.arrivalDate')} <span className="required-field">*</span></label>
                  </div>
                  <div className="form-floating">
                    <input
                      type="time"
                      className="form-control"
                      id="arrivalTime"
                      name="arrivalTime"
                      value={formData.arrivalTime}
                      onChange={handleChange}
                      required
                      disabled={!canEdit}
                    />
                    <label htmlFor="arrivalTime">{t('missionForm.fields.arrivalTime')} <span className="required-field">*</span></label>
                  </div>
                </div>
              </div>

              {userType === 'transporteur' && (
                <div className="form-section">
                  <h3 className="section-title">
                    <i className="bi bi-person-fill"></i> {t('client.donneurDordre')}
                  </h3>
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="clientId"
                      name="clientId"
                      value={formData.clientId}
                      onChange={handleChange}
                      disabled={!canEdit}
                    >
                      <option value="">{t('client.forMe')}</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.prenom} {client.nom} {client.entreprise ? `(${client.entreprise})` : ''}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="clientId">{t('client.selectClient')}</label>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Cargo & Details */}
            <div className="form-column">
              <div className="form-section">
                <h3 className="section-title">
                  <i className="bi bi-box-seam"></i> {t('missionForm.sections.cargo')}
                </h3>
                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <select
                        className="form-select"
                        id="cargoType"
                        name="cargoType"
                        value={formData.cargoType}
                        onChange={handleChange}
                        required
                        disabled={!canEdit}
                      >
                        <option value="">{t('missionForm.placeholders.select')}</option>
                        <option value="BENNES">{t('missionForm.cargoTypes.bennes')}</option>
                        <option value="CITERNES">{t('missionForm.cargoTypes.citernes')}</option>
                        <option value="CONTAINERS">{t('missionForm.cargoTypes.containers')}</option>
                        <option value="DEMENAGEMENTS">{t('missionForm.cargoTypes.demenagements')}</option>
                        <option value="MARCHANDISES_GENERALES">{t('missionForm.cargoTypes.marchandisesGenerales')}</option>
                        <option value="TEMPERATURE_DIRIGEE">{t('missionForm.cargoTypes.temperatureDirigee')}</option>
                        <option value="EXPRESSE_ENVELOPPE">{t('missionForm.cargoTypes.expresseEnveloppe')}</option>
                        <option value="EXPRESSE_COLIS">{t('missionForm.cargoTypes.expresseColis')}</option>
                        <option value="MESSAGERIE_COURSES">{t('missionForm.cargoTypes.messagerieCourses')}</option>
                        <option value="TRANSPORTS_EXCEPTIONNEL">{t('missionForm.cargoTypes.transportsExceptionnel')}</option>
                        <option value="TRANSPORTS_VOLUMINEUX">{t('missionForm.cargoTypes.transportsVolumineux')}</option>
                      </select>
                      <label htmlFor="cargoType">{t('missionForm.fields.cargoType')} <span className="required-field">*</span></label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      {currentVehicleOptions.length > 0 && currentVehicleOptions[0] !== 'Neant' ? (
                        <select
                          className="form-select"
                          id="vehicleType"
                          name="vehicleType"
                          value={formData.vehicleType}
                          onChange={handleChange}
                          required
                          disabled={!canEdit}
                        >
                          <option value="">{t('missionForm.placeholders.select')}</option>
                          {currentVehicleOptions.map((opt) => (
                            <option key={opt} value={opt}>{t(`missionForm.vehicleTypes.${opt}`)}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          id="vehicleType"
                          name="vehicleType"
                          value={formData.vehicleType}
                          onChange={handleChange}
                          required
                          placeholder={t('missionForm.placeholders.vehicleType')}
                          disabled={!canEdit}
                        />
                      )}
                      <label htmlFor="vehicleType">
                        {t('missionForm.fields.vehicleType')} <span className="required-field">*</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="weight"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.1"
                        placeholder="Tonne"
                        disabled={!canEdit}
                      />
                      <label htmlFor="weight">{t('missionForm.fields.weight')}</label>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="length"
                        name="length"
                        value={formData.length}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.1"
                        placeholder="m"
                        disabled={!canEdit}
                      />
                      <label htmlFor="length">{t('missionForm.fields.length')}</label>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="volume"
                        name="volume"
                        value={formData.volume}
                        onChange={handleChange}
                        min="0"
                        step="0.1"
                        placeholder="m3"
                        disabled={!canEdit}
                      />
                      <label htmlFor="volume">{t('missionForm.fields.volume')}</label>
                    </div>
                  </div>
                </div>

                <div className="checkbox-group mt-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <label className="form-check-label fw-bold small text-uppercase text-muted mb-0">
                      {t('missionForm.fields.dangerousMaterial')} <span className="required-field">*</span>
                    </label>
                    <div>
                      <div className="form-check form-check-inline mb-0">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="isDangerousMaterialYes"
                          name="isDangerousMaterial"
                          value="true"
                          checked={formData.isDangerousMaterial === true}
                          onChange={handleChange}
                          required
                          disabled={!canEdit}
                        />
                        <label className="form-check-label" htmlFor="isDangerousMaterialYes">
                          {t('common.yes')}
                        </label>
                      </div>
                      <div className="form-check form-check-inline mb-0">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="isDangerousMaterialNo"
                          name="isDangerousMaterial"
                          value="false"
                          checked={formData.isDangerousMaterial === false}
                          onChange={handleChange}
                          required
                          disabled={!canEdit}
                        />
                        <label className="form-check-label" htmlFor="isDangerousMaterialNo">
                          {t('common.no')}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">
                  <i className="bi bi-card-text"></i> {t('missionForm.sections.additional')}
                </h3>
                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control"
                        id="referenceNumber"
                        name="referenceNumber"
                        value={formData.referenceNumber}
                        onChange={handleChange}
                        placeholder="REF-..."
                        disabled={!canEdit}
                      />
                      <label htmlFor="referenceNumber">{t('missionForm.fields.referenceNumber')}</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group">
                      <div className="form-floating flex-grow-1">
                        <input
                          type="number"
                          className="form-control"
                          id="price"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          placeholder={t('missionForm.placeholders.price')}
                          disabled={!canEdit}
                          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        />
                        <label htmlFor="price">{t('missionForm.fields.price')}</label>
                      </div>
                      <select
                        className="form-select bg-light"
                        id="currency"
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        disabled={!canEdit}
                        style={{ maxWidth: '5.625rem', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, fontWeight: '600' }}
                      >
                        <option value="EUR">EUR</option>
                        <option value="MAD">MAD</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-floating mt-3">
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder={t('missionForm.placeholders.description')}
                    style={{ height: '5rem', minHeight: '5rem' }}
                    disabled={!canEdit}
                  />
                  <label htmlFor="description">{t('missionForm.fields.description')}</label>
                </div>
              </div>
            </div>

            {/* OCR Document Upload Zone - RemorqueForm style */}
            {!isEditMode && (
              <div className="col-12 mt-4">
                <hr className="text-muted opacity-25" />
                <h3 className="h6 fw-bold mb-3 text-uppercase small text-muted d-flex align-items-center gap-2">
                  <FaBrain className="text-primary" /> Extraction automatique par IA
                </h3>
                <div
                  className="dropzone p-4 bg-light border border-2 border-dashed rounded-3 text-center"
                  onDrop={handleOcrDrop}
                  onDragOver={handleOcrDragOver}
                  style={{ cursor: 'pointer', borderColor: ocrLoading ? '#0d6efd' : undefined }}
                >
                  <input
                    id="missionOcrFileInput"
                    type="file"
                    accept=".pdf,image/*"
                    className="d-none"
                    onChange={handleOcrFileInputChange}
                  />
                  {ocrLoading ? (
                    <>
                      <Spinner animation="border" variant="primary" className="mb-2" />
                      <p className="text-primary fw-bold mb-0">Extraction en cours...</p>
                    </>
                  ) : ocrFileName ? (
                    <>
                      <i className="bi bi-file-earmark-check-fill fs-1 text-success mb-2 d-block"></i>
                      <p className="text-success fw-bold mb-1">{ocrFileName}</p>
                      <p className="text-muted small mb-2">Document traité avec succès</p>
                      <label htmlFor="missionOcrFileInput" className="btn btn-sm btn-outline-primary">
                        Changer de document
                      </label>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-arrow-up fs-1 text-primary mb-2 d-block"></i>
                      <label htmlFor="missionOcrFileInput" className="btn btn-sm btn-primary mb-2">
                        Parcourir
                      </label>
                      <p className="text-muted small mb-0">
                        Glissez-déposez un document (PDF, image) pour remplir automatiquement les champs
                      </p>
                    </>
                  )}
                </div>
                {ocrError && (
                  <div className="alert alert-warning mt-2 mb-0 py-2" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>{ocrError}
                  </div>
                )}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => navigate('/missions')}
              >
                {t('missionForm.actions.cancel')}
              </button>
              {canEdit && (
                <button
                  type="submit"
                  className="btn btn-save"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {t('missionForm.actions.saving')}
                    </>
                  ) : (
                    t('missionForm.actions.save')
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      <Modal
        show={showAnalysisModal}
        onHide={() => setShowAnalysisModal(false)}
        size="lg"
        centered
        className="acc-modal-premium"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaBrain className="text-primary" /> {t('common.aiAnalyseReport') || 'Rapport d\'Analyse IA'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4 pb-5">
          {analysisLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">{t('common.aiAnalyseLoading') || 'Analyse de la mission en cours...'}</p>
            </div>
          ) : (
            <div className="acc-analysis-content fade-in">
              <div
                className="p-4 rounded-4 bg-light border-start border-4 border-primary shadow-sm"
                style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7', fontSize: '1.05rem', color: '#2c3e50' }}
              >
                {analysisResult}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" className="rounded-pill px-4" onClick={() => setShowAnalysisModal(false)}>
            {t('common.close') || 'Fermer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* OCR Preview Modal */}
      <Modal
        show={showOcrModal}
        onHide={() => setShowOcrModal(false)}
        size="lg"
        centered
        className="acc-modal-premium"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <FaBrain className="text-primary" /> Données extraites par IA
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pt-3 pb-4">
          <p className="text-muted mb-4">
            Voici les informations extraites depuis votre document. Vérifiez-les avant de les appliquer au formulaire.
          </p>
          {ocrExtractedFields && (() => {
            const f = ocrExtractedFields;
            const rows = [
              { label: 'Départ', value: f.departureLocation },
              { label: 'Arrivée', value: f.arrivalLocation },
              { label: 'Date départ', value: f.departureDate },
              { label: 'Date arrivée', value: f.arrivalDate },
              { label: 'Référence', value: f.referenceNumber },
              { label: 'Type de cargaison', value: f.cargoType },
              { label: 'Type de véhicule', value: f.vehicleType },
              { label: 'Poids (t)', value: f.weight },
              { label: 'Longueur (m)', value: f.length },
              { label: 'Volume (m³)', value: f.volume },
              { label: 'Prix', value: f.price },
              { label: 'Devise', value: f.currency },
              { label: 'Description', value: f.description },
              { label: 'Matière dangereuse', value: f.dangerousMaterial != null ? (f.dangerousMaterial ? 'Oui' : 'Non') : null },
            ].filter(r => r.value != null && r.value !== '');
            return rows.length === 0 ? (
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Aucune donnée n'a pu être extraite de ce document.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '35%' }}>Champ</th>
                      <th>Valeur extraite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        <td className="fw-semibold text-muted small text-uppercase">{row.label}</td>
                        <td className="fw-bold">{String(row.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="border-0 d-flex justify-content-between">
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => { setShowOcrModal(false); setOcrFileName(null); }}>
            Annuler
          </Button>
          <Button variant="primary" className="rounded-pill px-4 d-flex align-items-center gap-2" onClick={handleConfirmOcr}>
            <i className="bi bi-check2-circle"></i> Appliquer au formulaire
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default MissionForm;